import type { ErrorEvent, EventHint, StackFrame } from '@sentry/browser'

// Errors whose message alone shows they are not ours, usually because they arrive without a stack trace to check.
// Sentry matches a string as a substring of the message and a regex as is, so anchor regexes.
export const ignoreErrors: (string | RegExp)[] = [
  // A reading/writing-assistant extension rejects its own RPC timeouts with a bare string, so there is no
  // stacktrace for the extension-frame check to see.
  /^Non-Error promise rejection captured with value: Request timeout /,
  // WebKit deletes Firebase Auth's IndexedDB from under it in embedded webviews (Apple Mail, in-app browsers)
  // opening /sign_in. None of our own code uses IndexedDB.
  'Database deleted by request of the user',
  'The database connection is closing',
  // A password manager's autofill script runs without a script URL, so its frames are all `<anonymous>`.
  /^Cannot set properties of undefined \(setting 'unknown'\)$/,
  // Firebase Auth settles a sign-in popup when the user closes it or opens another one, and its own pending
  // origin check or IdP request then rejects the settled operation a second time. signInWithPopup has already
  // rejected to us by then, so the unhandled assertion has no one left to tell.
  /^INTERNAL ASSERTION FAILED: Pending promise was never set$/,
  // Chrome's DecompressionStream rejects with this when deflate data has trailing bytes, as PDF streams often do. A
  // script injected into the page, such as an extension reading a PDF the page links to, inflates with it. Neither our
  // code nor any of our dependencies uses DecompressionStream, and the rejection arrives without a stack trace.
  /^Junk found after end of compressed data\.$/,
]

// WebKit files the scripts a browser injects into the page, such as the translator and autofill of Chrome on iOS,
// under the URL of the page itself. We render no inline scripts (see config/initializers/content_security_policy.rb),
// so every frame of ours points to a script file, and a stack trace that only ever ran in the document is not ours.
function ranOnlyInDocument(frames: StackFrame[]): boolean {
  const scriptFrames = frames.filter((frame) => frame.filename !== '[native code]')
  return scriptFrames.length > 0 && scriptFrames.every((frame) => isDocumentUrl(frame.filename ?? ''))
}

function isDocumentUrl(url: string): boolean {
  return url.startsWith(`${window.location.origin}/`) && !/\.m?js([?#]|$)/.test(url)
}

// Sentry appends the host to the browser's network error message
const NETWORK_ERROR = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)( \([^)]+\))?$/

// Errors Turbo has already handled but still lets escape as unhandled rejections.
const turboHandledErrors = new WeakSet<Error>()

export function trackTurboHandledErrors() {
  // When the connection drops, Turbo announces the failed fetch of a visit, frame or form with this event, handles it
  // (a visit falls back to a full page load) and then rethrows it as an unhandled rejection anyway.
  document.addEventListener('turbo:fetch-request-error', (event) => {
    const { error } = event.detail
    if (error instanceof TypeError && NETWORK_ERROR.test(error.message)) turboHandledErrors.add(error)
  })

  // A click while a visit's response body is still downloading makes Turbo cancel that visit for the new one. Turbo
  // reads the body without catching, so the abort escapes as an unhandled rejection although the new visit goes ahead.
  document.addEventListener('turbo:before-fetch-request', (event) => {
    const { signal } = event.detail.fetchOptions
    signal?.addEventListener(
      'abort',
      () => {
        if (signal.reason instanceof Error) turboHandledErrors.add(signal.reason)
      },
      { once: true },
    )
  })
}

export function isNoise(event: ErrorEvent, hint: EventHint): boolean {
  const error = hint.originalException
  if (error instanceof Error && turboHandledErrors.has(error)) return true

  const stacktraces = event.exception?.values?.map((exception) => exception.stacktrace?.frames ?? []) ?? []
  return stacktraces.some(ranOnlyInDocument)
}
